import React, { FC, useCallback, useEffect, useMemo, useState } from 'react'
import { CrudFilters, getDefaultFilter } from '@refinedev/core'
import { List, useTable } from '@refinedev/antd'
import '../styles/index.css'
import { Row } from '../components/interface'
import PaymentStatus from '../../types/paymentStatus.type'
import dayjs from 'dayjs'
import { TableForm } from '../crypto-withdrawal/tableForm'
import { StatusFilter } from '../components/StatusFilter'
import { getColumns } from './InvoiceReceivablesColumns'
import { Table } from 'antd'
import { EditInvoiceReceivable } from './EditInvoiceReceivableV2'
import { useIsAdminRole } from '../../utils/hooks/useIdentityAdmin.hook'

const getFilters = (data: unknown): CrudFilters => {
  const { searchValue, createdAt, status } = data as {
    searchValue: string
    createdAt?: Date[] | null
    status: PaymentStatus
  }
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone
  return [
    {
      field: '',
      operator: 'contains',
      value: searchValue,
    },
    {
      field: '_status',
      operator: 'eq',
      value: PaymentStatus[status] ? status : undefined,
    },
    {
      field: '',
      operator: 'gte',
      value: createdAt
        ? dayjs(createdAt[0])
            .startOf('day')
            .toISOString()
        : undefined,
    },
    {
      field: '',
      operator: 'lte',
      value: createdAt
        ? dayjs(createdAt[1])
            .endOf('day')
            .toISOString()
        : undefined,
    },
    {
      field: '_timeZone',
      operator: 'eq',
      value: tz,
    },
  ]
}

export const InvoiceReceivables: FC = () => {
  const isAdminRole = useIsAdminRole()
  const {
    tableProps,
    searchFormProps,
    filters,
    tableQueryResult: { refetch },
  } = useTable<Row>({
    syncWithLocation: true,
    pagination: { pageSize: 10 },
    onSearch: getFilters,
  })

  const [newViewedIds, setNewViewedIds] = useState<number[]>([])

  useEffect(() => {
    setNewViewedIds([])
  }, [tableProps.dataSource])
  const [editingId, setEditingId] = useState<number>()
  const onClose = useCallback(() => {
    setEditingId(undefined)
  }, [])

  const initialValues = useMemo(() => {
    const start = getDefaultFilter('', filters, 'gte')
    const end = getDefaultFilter('', filters, 'lte')
    return {
      searchValue: getDefaultFilter('', filters),
      createdAt: start && end ? [dayjs(start), dayjs(end)] : undefined,
    }
  }, [filters])

  const statusFilterValue = useMemo(
    () => getDefaultFilter('_status', filters),
    [filters]
  )

  const columns = useMemo(() => getColumns(newViewedIds), [newViewedIds])

  return (
    <>
      <List title='Invoices (Receivables)'>
        <TableForm
          {...searchFormProps}
          initialValues={initialValues}
          onSubmit={searchFormProps.form?.submit}
        />
        <StatusFilter {...searchFormProps} defaultValue={statusFilterValue} />
        <Table
          {...tableProps}
          rowKey='id'
          columns={columns as any}
          dataSource={tableProps.dataSource}
          onRow={(record) => {
            return {
              onClick: () => {
                setEditingId(Number(record.id))
                if (record['rowNotViewed']) {
                  setNewViewedIds((newViewedIds) => [
                    ...newViewedIds,
                    Number(record.id),
                  ])
                }
              },
            }
          }}
          rowClassName={(record) =>
            isAdminRole &&
            record['rowNotViewed'] &&
            !newViewedIds.includes(record.id as number)
              ? 'ant-table-highlighted-row'
              : ''
          }
        />
      </List>
      <EditInvoiceReceivable
        editingId={editingId}
        onClose={onClose}
        onComplete={refetch}
      />
    </>
  )
}
